// React Imports
import { ReactElement, useEffect, useState } from "react";

// Third Party Libraries Imports
import TextArea from "antd/es/input/TextArea";
import { DatePicker, Form, Select } from "antd";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

// Local Imports
import Label from "../../../../components/label/label.component";
import ClsodureRequestService from "../../closure-request.service";
import ClosureRequest from "../../../../models/closure-request.model";
import { Status } from "../../../../models/enum";

interface takeActionModalProps {
  selectedRequest: ClosureRequest | undefined;
  onSuccess: () => void;
  onCancel: () => void;
}

const TakeClosureAction: React.FC<takeActionModalProps> = ({
  selectedRequest,
  onSuccess,
  onCancel,
}: takeActionModalProps): ReactElement => {
  const closureRequestService = new ClsodureRequestService();
  const user = useSelector((state: any) => state?.user);
  const [form] = Form.useForm();
  const [submitForm, setSubmitForm] = useState(false);
  const values = Form.useWatch([], form);

  useEffect(() => {
    form.validateFields({ validateOnly: false }).then(
      () => {
        setSubmitForm(true);
      },
      () => {
        setSubmitForm(false);
      }
    );
  }, [values]);

  const onSubmit = async (values: {
    status: Status;
    statusNotes: string;
    closureDate?: any;
  }): Promise<void> => {
    const response = await closureRequestService.takeClosureRequestAction(
      selectedRequest?.id as number,
      values.status,
      values.statusNotes,
      values.closureDate?.format("YYYY-MM-DD")
    );
    if (response) {
      onSuccess();
    }
  };

  return (
    <section className="flex flex-col px-[5%] w-full box-border">
      <h3 className=" text-black-500 text-2xl font-semibold tracking-[0.24px] mb-5">
        Take action
      </h3>
      <Form form={form} onFinish={onSubmit} className="flex flex-col">
        <div className="flex-col w-full">
          <Label text="Closure action" />
          <Form.Item
            name="status"
            rules={[
              {
                required: true,
                message: "Please select closure action!",
              },
            ]}
          >
            <Select
              placeholder="Select closure action"
              options={[
                {
                  value: Status.Approved,
                  label: "Approve",
                },
                {
                  value: Status.Rejected,
                  label: "Reject",
                },
              ]}
              size="large"
              className=" placeholder:text-xs"
            />
          </Form.Item>
        </div>

        {user?.withdrawGroup === "OPS" && (
          <div className="flex-col w-full">
            <Label text="Closure Date" required />
            <Form.Item
              name="closureDate"
              initialValue={dayjs()}
              rules={[
                {
                  required: true,
                  message: "Please select closure date!",
                },
              ]}
            >
              <DatePicker
                format={"YYYY-MM-DD"}
                placeholder="Select closure date"
                onChange={(e: any) => e?.format("YYYY-MM-DD")}
              />
            </Form.Item>
          </div>
        )}

        <div className="flex flex-col">
          <Label text="Notes" />
          <Form.Item
            name="statusNotes"
            rules={[
              {
                required: true,
                message: "Please input notes!",
              },
            ]}
          >
            <TextArea rows={7} placeholder="Enter notes" />
          </Form.Item>
        </div>

        <div className="flex items-center justify-between mt-5">
          <button
            className="w-[48%] border-[1px] border-blue-500 text-blue-500 rounded-lg py-3 font-medium text-base"
            onClick={onCancel}
            type="button"
          >
            Cancel
          </button>
          <button
            className="disabled:opacity-50 disabled:cursor-not-allowed font-medium text-base w-[48%] border-[1px] border-blue-500 text-white bg-blue-500 rounded-lg py-3"
            disabled={!submitForm}
          >
            Submit
          </button>
        </div>
      </Form>
    </section>
  );
};

export default TakeClosureAction;
